import { APP_NAME } from '@circa/shared/config';
import {
  ArrowsClockwise,
  CloudCheck,
  CloudSlash,
  CloudWarning,
  LockSimple,
  MagnifyingGlass,
  Plus,
  PushPin,
  SidebarSimple,
  X,
} from '@phosphor-icons/react';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isCapacitor, isNativeApp, useIsMobile } from '@/core/platform';
import { remove } from '@/core/repo';
import {
  activateTab,
  closeOtherTabs,
  closeTab,
  cycleTab,
  moveTab,
  navigate,
  newTab,
  pinTab,
  type Tab,
  useRoute,
  useRouter,
} from '@/core/router';
import { lock } from '@/core/session';
import { usePref } from '@/core/settings';
import { useList } from '@/core/store';
import { syncNow, useSyncState } from '@/core/sync';
import { relativeShort, setZoneName } from '@/core/time';
import { syncConnections } from '@/modules/connections/run';
import { logItem } from '@/modules/food/model';
import { useProfile } from '@/modules/health/profile';
import { ZoneSensePrompt } from '@/modules/sleep/ZoneSense';
import { cx, IconButton, Spinner, setHeaderExtra, useClockKey } from '@/ui/components';
import { useDrawerDrag, useHotkey } from '@/ui/gestures';
import { OverlayHost, openMenu, Sheet, toast } from '@/ui/overlays';
import { closeDrawer, openDrawer, setShell, useShell } from '@/ui/shellState';
import { UpdatePrompt } from '@/ui/updates';
import { ZoneChangePrompt, ZoneIndicator } from '@/ui/zone';
import { DEFAULT_BOTTOM_NAV, MODULES, moduleById, moduleFor } from './modules';

setHeaderExtra(ZoneIndicator);

export function AppShell() {
  // Calling home's zone by the home city ("Fort Myers", not "New York") everywhere.
  const [profile] = useProfile();
  setZoneName(profile.homeZone, profile.homeName);
  // Midnight or a new time zone (travel) while open: re-render so "today" and times follow.
  useClockKey();
  const mobile = useIsMobile();
  const route = useRoute();
  const shell = useShell();
  const [collapsed, setCollapsed] = usePref('sidebarCollapsed', false);
  const mod = moduleFor(route.path);
  const Page = mod.page;
  // The tab strip only appears once there's a second tab (Ctrl+T, "Open in new tab",
  // middle-click); with one tab it would just take height.
  const tabCount = useRouter().tabs.length;
  const tabs = !mobile && tabCount > 1;

  const drawerRef = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  const setDrawer = useCallback((open: boolean) => (open ? openDrawer() : closeDrawer()), []);
  useDrawerDrag({
    enabled: mobile,
    open: shell.drawerOpen,
    setOpen: setDrawer,
    drawer: drawerRef,
    scrim: scrimRef,
  });
  useHotkey('mod+k', () => setShell({ paletteOpen: true }));
  useHotkey('mod+p', () => setShell({ paletteOpen: true }));
  useHotkey('mod+t', () => newTab());
  useHotkey('mod+w', () => closeTab(route.tabId));
  useHotkey('ctrl+tab', () => cycleTab(1));
  useHotkey('ctrl+shift+tab', () => cycleTab(-1));
  useHotkey('mod+\\', () => setCollapsed(!collapsed));
  useHotkey('mod+shift+l', () => void lock());
  useHotkey('alt+n', () => navigate('/food?add=1'));

  // Close the drawer whenever the route changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: route change is the trigger
  useEffect(() => {
    if (shell.drawerOpen) closeDrawer();
  }, [route.path]);

  // Hide the bottom bar while typing on a phone (keyboard open). The Android app asks the
  // keyboard itself: closing it with the cursor still in a field (Back) must bring back the
  // space for the system navigation bar. Browsers: a focused field means the keyboard is up.
  useEffect(() => {
    if (!mobile) return;
    const isField = (el: Element | null) =>
      !!el && (el.matches('input, textarea, select') || (el as HTMLElement).isContentEditable);
    let keyboard = !isCapacitor;
    const on = () =>
      document.body.classList.toggle('kb-open', keyboard && isField(document.activeElement));
    const later = () => setTimeout(on, 50);
    document.addEventListener('focusin', on);
    document.addEventListener('focusout', later);
    let alive = true;
    let removeListeners: (() => void) | undefined;
    if (isCapacitor)
      void import('@capacitor/keyboard')
        .then(async ({ Keyboard }) => {
          const show = await Keyboard.addListener('keyboardWillShow', () => {
            keyboard = true;
            on();
          });
          const hide = await Keyboard.addListener('keyboardWillHide', () => {
            keyboard = false;
            on();
          });
          removeListeners = () => {
            void show.remove();
            void hide.remove();
          };
          if (!alive) removeListeners();
        })
        .catch(() => {
          keyboard = true;
        });
    return () => {
      alive = false;
      removeListeners?.();
      document.removeEventListener('focusin', on);
      document.removeEventListener('focusout', later);
      document.body.classList.remove('kb-open');
    };
  }, [mobile]);

  return (
    <div className="shell">
      {!mobile && <Sidebar rail={collapsed} onToggleRail={() => setCollapsed(!collapsed)} />}
      {mobile && (
        <>
          <div
            ref={scrimRef}
            className={cx('drawer-scrim', shell.drawerOpen && 'drawer-scrim--open')}
            onClick={closeDrawer}
          />
          <div
            ref={drawerRef}
            className={cx('drawer', shell.drawerOpen && 'drawer--open')}
            aria-hidden={!shell.drawerOpen}
          >
            <Sidebar rail={false} />
          </div>
        </>
      )}
      <div className="main">
        {tabs && <TabBar />}
        <div className="content">
          <Suspense fallback={<PageFallback />}>
            <Page key={route.tabId + mod.id} route={route} />
          </Suspense>
        </div>
        {mobile && <BottomNav active={mod.id} />}
      </div>
      {shell.tabSwitcherOpen && <TabSwitcher />}
      {shell.paletteOpen && <Palette />}
      <ZoneChangePrompt />
      <ZoneSensePrompt />
      <UpdatePrompt />
      <OverlayHost />
    </div>
  );
}

function PageFallback() {
  return (
    <div className="center" style={{ flex: 1 }}>
      <Spinner />
    </div>
  );
}

// ------------------------------------------------------------------ sidebar

function SyncIndicator() {
  const s = useSyncState();
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);
  // Short label (the sidebar footer is narrow); the details are in the tooltip.
  const label =
    s.status === 'off'
      ? 'Local'
      : s.status === 'syncing'
        ? 'Syncing'
        : s.status === 'offline'
          ? 'Offline'
          : s.status === 'error'
            ? 'Error'
            : 'Synced';
  const detail =
    s.status === 'off'
      ? 'This device only: set up sync in Settings'
      : s.status === 'offline'
        ? `Offline${s.pending ? `, ${s.pending} change${s.pending === 1 ? '' : 's'} waiting to sync` : ''}`
        : s.status === 'idle' && s.lastSyncAt
          ? `Synced ${relativeShort(s.lastSyncAt) === 'now' ? 'just now' : `${relativeShort(s.lastSyncAt)} ago`}. Click to sync now.`
          : label;
  const icon =
    s.status === 'syncing' ? (
      <ArrowsClockwise size={16} style={{ animation: 'e-spin 1s linear infinite' }} />
    ) : s.status === 'offline' || s.status === 'off' ? (
      <CloudSlash size={16} />
    ) : s.status === 'error' ? (
      <CloudWarning size={16} color="var(--danger)" />
    ) : (
      <CloudCheck size={16} />
    );
  return (
    <button
      type="button"
      className="sb__sync"
      title={s.error ?? detail}
      onClick={() => (s.status === 'off' ? navigate('/settings/sync') : void syncNow())}
    >
      {icon}
      <span className="truncate">{label}</span>
    </button>
  );
}

function Sidebar({ rail, onToggleRail }: { rail: boolean; onToggleRail?: () => void }) {
  const route = useRoute();
  const mobile = useIsMobile();
  const active = moduleFor(route.path);
  const Section = active.sidebar;
  const groups: { id: string; label: string }[] = [{ id: 'main', label: 'Health' }];
  return (
    <nav className={cx('sb', rail && 'sb--rail')} aria-label="Main">
      <div className="sb__top">
        {!rail && (
          <div className="sb__brand">
            <div className="sb__logo" />
            {APP_NAME}
          </div>
        )}
        {!rail && (
          <IconButton
            label="Search (Ctrl+K)"
            size="sm"
            onClick={() => setShell({ paletteOpen: true, drawerOpen: false })}
          >
            <MagnifyingGlass size={18} />
          </IconButton>
        )}
        {onToggleRail && (
          <IconButton
            label={rail ? 'Expand sidebar' : 'Collapse sidebar'}
            size="sm"
            onClick={onToggleRail}
          >
            <SidebarSimple size={18} />
          </IconButton>
        )}
      </div>
      {
        <div className="sb__scroll">
          {groups.map((g) => (
            <div key={g.id} className="sb__group">
              {g.id !== 'main' && <div className="sb__label">{g.label}</div>}
              {MODULES.filter((m) => m.group === g.id && !(rail && m.id === 'settings')).map(
                (m) => {
                  const Icon = m.icon;
                  const isActive = m.id === active.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      className={cx('sb__item', isActive && 'sb__item--active')}
                      title={rail ? m.label : undefined}
                      onClick={(e) => navigate(`/${m.id}`, { newTab: e.ctrlKey || e.metaKey })}
                      onAuxClick={(e) =>
                        e.button === 1 && navigate(`/${m.id}`, { newTab: true, background: true })
                      }
                    >
                      <Icon size={mobile ? 22 : 19} weight={isActive ? 'fill' : 'regular'} />
                      <span>{m.label}</span>
                    </button>
                  );
                },
              )}
              {g.id === 'main' && Section && !rail && (
                <div className="sb__section">
                  <Suspense fallback={null}>
                    <Section />
                  </Suspense>
                </div>
              )}
            </div>
          ))}
        </div>
      }
      <div className="sb__foot">
        <SyncIndicator />
        <IconButton label="Lock" size="sm" onClick={() => void lock()}>
          <LockSimple size={18} />
        </IconButton>
        <IconButton
          label="Settings"
          size="sm"
          active={active.id === 'settings'}
          onClick={() => navigate('/settings')}
        >
          {(() => {
            const Icon = moduleById.get('settings')!.icon;
            return <Icon size={18} />;
          })()}
        </IconButton>
      </div>
    </nav>
  );
}

// ------------------------------------------------------------------ tabs

function tabLabel(t: Tab): string {
  return t.title || moduleFor(t.path).label;
}

function TabBar() {
  const { tabs, activeId } = useRouter();
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const tabMenu = (t: Tab, x: number, y: number) =>
    openMenu({ x, y }, [
      {
        label: t.pinned ? 'Unpin tab' : 'Pin tab',
        icon: <PushPin size={16} />,
        onClick: () => pinTab(t.id, !t.pinned),
      },
      { label: 'Duplicate tab', onClick: () => navigate(t.path, { newTab: true }) },
      { divider: true },
      {
        label: 'Close tab',
        icon: <X size={16} />,
        onClick: () => closeTab(t.id),
        shortcut: 'Ctrl+W',
      },
      { label: 'Close other tabs', onClick: () => closeOtherTabs(t.id) },
    ]);
  return (
    <div className="tabbar" role="tablist">
      {tabs.map((t, i) => {
        const Icon = moduleFor(t.path).icon;
        return (
          <div
            key={t.id}
            role="tab"
            aria-selected={t.id === activeId}
            className={cx(
              'tab',
              t.id === activeId && 'tab--active',
              t.pinned && 'tab--pinned',
              overIdx === i && dragIdx !== i && 'tab--dragover',
            )}
            title={tabLabel(t)}
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => {
              e.preventDefault();
              setOverIdx(i);
            }}
            onDragEnd={() => {
              setDragIdx(null);
              setOverIdx(null);
            }}
            onDrop={() => {
              if (dragIdx !== null && dragIdx !== i) moveTab(dragIdx, i);
              setDragIdx(null);
              setOverIdx(null);
            }}
            onMouseDown={(e) => {
              if (e.button === 0) activateTab(t.id);
            }}
            onAuxClick={(e) => e.button === 1 && closeTab(t.id)}
            onContextMenu={(e) => {
              e.preventDefault();
              tabMenu(t, e.clientX, e.clientY);
            }}
          >
            <Icon
              size={15}
              weight={t.id === activeId ? 'fill' : 'regular'}
              style={{ flex: 'none', color: t.id === activeId ? 'var(--accent)' : undefined }}
            />
            {!t.pinned && <span className="tab__title">{tabLabel(t)}</span>}
            {!t.pinned && (
              <button
                type="button"
                className="tab__close"
                aria-label="Close tab"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => closeTab(t.id)}
              >
                <X size={12} weight="bold" />
              </button>
            )}
          </div>
        );
      })}
      <IconButton
        label="New tab (Ctrl+T)"
        className="tabbar__new"
        size="sm"
        onClick={() => newTab()}
      >
        <Plus size={16} />
      </IconButton>
    </div>
  );
}

function TabSwitcher() {
  const { tabs, activeId } = useRouter();
  const close = useCallback(() => setShell({ tabSwitcherOpen: false }), []);
  return (
    <Sheet open onClose={close} title={`${tabs.length} open tabs`} fullHeight>
      <div className="tabsw">
        {tabs.map((t) => {
          const Icon = moduleFor(t.path).icon;
          return (
            <button
              key={t.id}
              type="button"
              className={cx('tabsw__card', t.id === activeId && 'tabsw__card--active')}
              onClick={() => {
                activateTab(t.id);
                close();
              }}
            >
              <Icon size={22} color="var(--accent)" />
              <div
                className="bold"
                style={{
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {tabLabel(t)}
              </div>
              <div className="xsmall muted">{moduleFor(t.path).label}</div>
              <IconButton
                label="Close tab"
                size="sm"
                className="tabsw__close"
                onClick={(e) => {
                  e.stopPropagation();
                  closeTab(t.id);
                }}
              >
                <X size={16} />
              </IconButton>
            </button>
          );
        })}
        <button
          type="button"
          className="tabsw__card center"
          style={{ alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)' }}
          onClick={() => {
            newTab();
            close();
          }}
        >
          <Plus size={26} />
          New tab
        </button>
      </div>
    </Sheet>
  );
}

// ------------------------------------------------------------------ bottom nav

function BottomNav({ active }: { active: string }) {
  const [ids] = usePref<string[]>('bottomNav', DEFAULT_BOTTOM_NAV);
  return (
    <nav className="bottomnav" aria-label="Areas">
      {ids
        .map((id) => moduleById.get(id))
        .filter((m) => !!m)
        .map((m) => {
          const Icon = m!.icon;
          const on = m!.id === active;
          return (
            <button
              key={m!.id}
              type="button"
              className={cx('bottomnav__item', on && 'bottomnav__item--active')}
              onClick={() => navigate(`/${m!.id}`)}
            >
              <span className="bottomnav__pill">
                <Icon size={24} weight={on ? 'fill' : 'regular'} />
              </span>
              {m!.label.replace('AI ', '')}
            </button>
          );
        })}
    </nav>
  );
}

// ------------------------------------------------------------------ command palette

interface PaletteItem {
  id: string;
  label: string;
  hint?: string;
  icon: React.ReactNode;
  run: (newTabToo: boolean) => void;
  rank: number;
}

function Palette() {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const foods = useList('foodItem');
  const listRef = useRef<HTMLDivElement>(null);
  const close = () => setShell({ paletteOpen: false });

  const items = useMemo<PaletteItem[]>(() => {
    const query = q.trim().toLowerCase();
    const out: PaletteItem[] = [];
    const score = (text: string) => {
      if (!query) return 1;
      const t = text.toLowerCase();
      if (t.startsWith(query)) return 3;
      if (t.includes(query)) return 2;
      return 0;
    };
    for (const m of MODULES) {
      const s = score(m.label);
      if (s) {
        const Icon = m.icon;
        out.push({
          id: `go:${m.id}`,
          label: `Go to ${m.label}`,
          icon: <Icon size={18} />,
          rank: s + 0.5,
          run: (nt) => navigate(`/${m.id}`, { newTab: nt }),
        });
      }
      if (m.newPath && m.newLabel) {
        const s2 = score(m.newLabel);
        if (s2) {
          const Icon = m.icon;
          out.push({
            id: `new:${m.id}`,
            label: m.newLabel,
            icon: <Plus size={18} />,
            hint: m.label,
            rank: s2 + 0.6,
            run: (nt) => navigate(m.newPath!, { newTab: nt }),
          });
          void Icon;
        }
      }
    }
    const extra: [string, () => void][] = [
      ['Lock app', () => void lock()],
      ['Sync now', () => void syncNow()],
      ['Sync Oura, Renpho and Trace now', () => void syncConnections({ force: true })],
    ];
    for (const [label, fn] of extra) {
      const s = score(label);
      if (s && query)
        out.push({
          id: `cmd:${label}`,
          label,
          icon: <ArrowsClockwise size={18} />,
          rank: s,
          run: fn,
        });
    }
    if (query) {
      // Foods you've logged before: Enter logs one serving now.
      const FoodIcon = moduleById.get('food')!.icon;
      for (const f of foods) {
        const s = score(f.name);
        if (s)
          out.push({
            id: f.id,
            label: `Log ${f.name}`,
            hint: `${Math.round(f.kcal)} cal${f.serving ? ` · ${f.serving}` : ''}`,
            icon: <FoodIcon size={18} />,
            rank: s + Math.min(f.uses, 50) / 100,
            run: () => {
              const e = logItem(f);
              toast(`Logged ${e.name}`, {
                action: { label: 'Undo', onClick: () => remove('food', e.id) },
              });
            },
          });
      }
    }
    return out.sort((a, b) => b.rank - a.rank).slice(0, 60);
  }, [q, foods]);

  useEffect(() => setSel(0), []);
  useEffect(() => {
    listRef.current?.querySelector('.palette__item--sel')?.scrollIntoView({ block: 'nearest' });
  }, []);

  return (
    <>
      <div className="e-overlay" onClick={close} />
      <div className="palette" role="dialog" aria-label="Search">
        <input
          className="palette__input"
          placeholder="Log a food you’ve had before, or type a command"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSel((s) => Math.min(items.length - 1, s + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSel((s) => Math.max(0, s - 1));
            } else if (e.key === 'Enter') {
              const it = items[sel];
              if (it) {
                close();
                it.run(e.ctrlKey || e.metaKey);
              }
            } else if (e.key === 'Escape') close();
          }}
        />
        <div className="palette__list" ref={listRef}>
          {items.map((it, i) => (
            <button
              key={it.id}
              type="button"
              className={cx('palette__item', i === sel && 'palette__item--sel')}
              onMouseMove={() => setSel(i)}
              onClick={(e) => {
                close();
                it.run(e.ctrlKey || e.metaKey);
              }}
            >
              {it.icon}
              <span className="grow truncate">{it.label}</span>
              {it.hint && <span className="xsmall muted">{it.hint}</span>}
            </button>
          ))}
          {!items.length && <div className="pad muted small">Nothing found.</div>}
        </div>
      </div>
    </>
  );
}

export { isNativeApp };
